import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { portalApi } from '../services/api'
import { resolveLoginRedirect } from '../services/roleContext'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'
import LanguageToggle from '../components/LanguageToggle'

export default function Login() {
  const navigate = useNavigate()
  const { login: setAuth } = useAuth()
  const { t } = useLanguage()
  const [params] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(() => {
    const e = params.get('error')
    if (e === 'no_code' || e === 'token_failed' || e === 'profile_failed' || e === 'google_failed') {
      return t('sp_login_google_failed', 'Google sign-in failed. Please try again or sign in with email.')
    }
    if (e === 'no_token') return t('sp_login_no_token', 'Sign-in did not complete. Please try again.')
    return ''
  })

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const { data } = await portalApi.post('/auth/login', { email, password })
      setAuth(data.user, data.token)
      // Role-aware redirect: super admins -> /admin, resellers -> /portal/reseller,
      // course merchants -> /portal (existing Stay & Play). The redirect is baked
      // into the login response by PlayStay-API so we don't need a separate
      // /api/auth/role-context call (which required an Apache /api/* proxy on
      // every environment). Fall back to the old resolver only for users whose
      // backend pre-dates this change.
      const redirect = data.user?.redirect || await resolveLoginRedirect()
      navigate(redirect)
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message)
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError(t('sp_login_server_unreachable', 'Could not reach the login server. Please check that PlayStay-API is running on port 3011.'))
      } else {
        setError(err.message || t('sp_login_failed_retry', 'Sign-in failed. Please try again.'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff 50%, var(--brand-100) 100%)',
      padding: 20,
    }}>
      <div className="card" style={{ padding: 40, width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <LanguageToggle />
        </div>
        <Link to="/" style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <img src="/integolf-logo.svg" alt="InteGolf" style={{ height: 44 }} />
        </Link>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }}>
            {t('sp_play_and_stay_portal_caps', 'PLAY AND STAY PORTAL')}
          </div>
          <h1 style={{ margin: 0, fontSize: 26, color: 'var(--secondary-950)' }}>{t('sp_welcome_back', 'Welcome back')}</h1>
        </div>

        {/* Google OAuth button */}
        <a
          href="/api/play-stay-portal/auth/google"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, padding: '12px', marginBottom: 16,
            background: '#fff', color: 'var(--secondary-800)',
            border: '1px solid var(--input-border)', borderRadius: 10,
            fontWeight: 600, fontSize: 14, textDecoration: 'none',
          }}
        >
          <GoogleIcon />
          {t('sp_sign_in_with_google', 'Sign in with Google')}
        </a>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          color: 'var(--text-muted)', fontSize: 12, margin: '16px 0 20px',
        }}>
          <div style={{ flex: 1, height: 1, background: 'var(--card-border)' }} />
          {t('or', 'or')}
          <div style={{ flex: 1, height: 1, background: 'var(--card-border)' }} />
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', marginBottom: 16,
            background: '#fef2f2', color: '#dc2626',
            border: '1px solid #fecaca', borderRadius: 8, fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>{t('email', 'Email')}</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={inputStyle}
            placeholder={t('sp_email_placeholder', 'you@yourgolfcourse.com')}
            autoFocus
          />

          <label style={{ ...labelStyle, marginTop: 16 }}>{t('password', 'Password')}</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            style={inputStyle}
            placeholder="••••••••"
          />

          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <Link to="/forgot-password" style={{ fontSize: 13, color: 'var(--brand-600)', fontWeight: 600, textDecoration: 'none' }}>
              {t('sp_forgot_password', 'Forgot password?')}
            </Link>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn btn-dark"
            style={{ width: '100%', marginTop: 24, padding: '14px', opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? t('sp_signing_in', 'Signing in...') : t('sp_sign_in', 'Sign In')}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: 'var(--text-muted)' }}>
          {t('sp_dont_have_account', 'Don\'t have an account?')} <Link to="/signup" style={{ fontWeight: 600, color: 'var(--brand-600)' }}>{t('sp_get_started', 'Get started')}</Link>
        </div>
      </div>
    </div>
  )
}

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--secondary-800)',
  marginBottom: 6,
}

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid var(--input-border)',
  borderRadius: 8,
  fontSize: 14,
  outline: 'none',
  fontFamily: 'inherit',
  color: 'var(--text)',
  boxSizing: 'border-box',
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  )
}
